import { useState } from "react";
import { cn } from "@veo/ui";

interface ImageSource {
  url?: string;
  srcSet?: string;
  alt?: string;
  width?: number | null;
  height?: number | null;
  placeholder?: string | null;
  mediaId?: string;
}

const MEDIA_BASE = import.meta.env.VITE_MEDIA_URL ?? "";

/**
 * An image that behaves well on a slow connection.
 *
 * Three things matter here and each is deliberate:
 *
 *   - `srcSet` and `sizes` let the browser fetch the smallest adequate file.
 *     Without them a phone downloads a 1600px image to paint it at 390px,
 *     which is the single largest avoidable cost on a page like this.
 *   - `width`/`height` reserve the space before the bytes arrive, so text does
 *     not jump as images load.
 *   - Everything below the fold is `loading="lazy"`; the hero is not, because
 *     lazily loading the largest element in the viewport delays the metric it
 *     is being measured on.
 */
export function ResponsiveImage({
  image,
  className,
  sizes = "100vw",
  priority = false,
  alt,
}: {
  image: ImageSource | null | undefined;
  className?: string;
  sizes?: string;
  priority?: boolean;
  alt?: string;
}) {
  const [loaded, setLoaded] = useState(false);

  if (!image?.url && !image?.mediaId) return null;

  const src = image.url ?? `${MEDIA_BASE}/${image.mediaId}`;
  // An explicit `alt` prop wins; otherwise the CMS value, defaulting to empty
  // (correct for decorative images, and better than inventing a description).
  const altText = alt ?? image.alt ?? "";

  return (
    <img
      src={src}
      {...(image.srcSet ? { srcSet: image.srcSet, sizes } : {})}
      alt={altText}
      width={image.width ?? undefined}
      height={image.height ?? undefined}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
      {...(priority ? { fetchPriority: "high" as const } : {})}
      // The page is server-rendered, so the browser can finish fetching the
      // image before React hydrates — the load event then fires with no
      // listener attached and the fade-in never triggers. The callback ref
      // runs at attach time and catches that case via `complete`.
      ref={(el) => {
        if (el?.complete) setLoaded(true);
      }}
      onLoad={() => setLoaded(true)}
      onError={() => setLoaded(true)}
      className={cn(
        "transition-opacity duration-500",
        loaded ? "opacity-100" : "opacity-0",
        className,
      )}
      // The inline placeholder shows instantly, so there is something to look
      // at rather than an empty rectangle.
      style={
        image.placeholder && !loaded
          ? { backgroundImage: `url(${image.placeholder})`, backgroundSize: "cover", opacity: 1 }
          : undefined
      }
    />
  );
}
